/**
 * Hooks for admin skills management.
 *
 * Provides data fetching for skills taxonomy with challenge counts
 * and discipline-based organization.
 */

import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../lib/api";
import type { DisciplineSlug, SkillKey } from "@jumpbean/shared";

// ----- Types -----

export interface SkillItem {
  id: string;
  disciplineId: string;
  skillKey: string;
  displayName: string;
  description?: string;
  parentSkillId: string | null;
  difficultyOrder: number;
  isActive: boolean;
}

export interface SkillWithCount extends SkillItem {
  challengeCount: number;
}

export interface DisciplineSkillGroup {
  disciplineId: string;
  disciplineSlug: DisciplineSlug;
  disciplineName: string;
  skills: SkillWithCount[];
  totalChallenges: number;
}

interface SkillsApiResponse {
  success: boolean;
  data: {
    skills: Array<{
      id: string;
      discipline_id: string;
      skill_key: string;
      display_name: string;
      description?: string;
      parent_skill_id: string | null;
      difficulty_order: number;
      is_active: boolean;
    }>;
    byDiscipline: Record<string, Array<{
      id: string;
      discipline_id: string;
      skill_key: string;
      display_name: string;
      difficulty_order: number;
      is_active: boolean;
    }>>;
    totalCount: number;
  };
}

interface ChallengeCountsResponse {
  success: boolean;
  data: {
    challenges: Array<{
      id: string;
      skillTags: string[];
    }>;
    pagination: {
      total: number;
    };
  };
}

// Discipline metadata for display
const DISCIPLINE_INFO: Record<string, { slug: DisciplineSlug; name: string }> = {
  math: { slug: "math", name: "Math" },
  spelling: { slug: "spelling", name: "Spelling" },
  patterns: { slug: "patterns", name: "Patterns" },
  typing: { slug: "typing", name: "Typing" },
  programming: { slug: "programming", name: "Programming" },
};

// ----- useAdminSkills Hook -----

export function useAdminSkills() {
  const [skillGroups, setSkillGroups] = useState<DisciplineSkillGroup[]>([]);
  const [allSkills, setAllSkills] = useState<SkillWithCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDisciplines, setExpandedDisciplines] = useState<Set<string>>(
    new Set(["math", "spelling", "patterns", "typing", "programming"])
  );

  const fetchSkillsData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch skills from API
      const skillsResult = await apiGet<SkillsApiResponse>("/api/admin/ai/skills");

      if (skillsResult.error) {
        throw new Error(skillsResult.error.message);
      }

      const skillsData = skillsResult.data?.data;
      if (!skillsData) {
        throw new Error("No skills data received");
      }

      // Fetch challenges to count per skill
      // We fetch all challenges with a large limit to get skill tag distribution
      const challengesResult = await apiGet<ChallengeCountsResponse>(
        "/api/admin/challenges?limit=1000&offset=0"
      );

      // Build skill counts from challenges
      const skillCounts: Record<string, number> = {};
      if (challengesResult.data?.data?.challenges) {
        for (const challenge of challengesResult.data.data.challenges) {
          for (const tag of challenge.skillTags || []) {
            skillCounts[tag] = (skillCounts[tag] || 0) + 1;
          }
        }
      }

      // Transform skills to our format with counts
      const transformedSkills: SkillWithCount[] = skillsData.skills.map((skill) => ({
        id: skill.id,
        disciplineId: skill.discipline_id,
        skillKey: skill.skill_key,
        displayName: skill.display_name,
        description: skill.description,
        parentSkillId: skill.parent_skill_id,
        difficultyOrder: skill.difficulty_order,
        isActive: skill.is_active,
        challengeCount: skillCounts[skill.skill_key] || 0,
      }));

      // Group skills by discipline
      // We need to map discipline_id to slug
      // The API returns byDiscipline keyed by discipline_id (UUID)
      // We'll organize by iterating through known disciplines and matching
      const disciplineGroups: DisciplineSkillGroup[] = [];
      const disciplineIds = new Set(transformedSkills.map((s) => s.disciplineId));

      // Create a mapping from the order of skills to guess discipline slug
      // Since skills are sorted by difficulty_order and grouped by discipline_id,
      // we can use the skill_key prefix to determine discipline
      const getDisciplineFromSkillKey = (skillKey: string): DisciplineSlug | null => {
        // Math skills
        if (
          skillKey.startsWith("counting_") ||
          skillKey.startsWith("number_") ||
          skillKey.startsWith("addition_") ||
          skillKey.startsWith("subtraction_") ||
          skillKey.startsWith("comparison_")
        ) {
          return "math";
        }
        // Spelling skills
        if (
          skillKey.startsWith("letter_") ||
          skillKey.startsWith("beginning_") ||
          skillKey.startsWith("ending_") ||
          skillKey.startsWith("vowel_") ||
          skillKey.startsWith("cvc_") ||
          skillKey.startsWith("sight_")
        ) {
          return "spelling";
        }
        // Pattern skills
        if (skillKey.startsWith("pattern_")) {
          return "patterns";
        }
        // Typing skills
        if (
          skillKey.startsWith("keyboard_") ||
          skillKey.startsWith("key_") ||
          skillKey.startsWith("home_row_") ||
          skillKey.startsWith("letter_sequences") ||
          skillKey.startsWith("simple_") ||
          skillKey.startsWith("short_") ||
          skillKey === "cvc_word_typing"
        ) {
          return "typing";
        }
        // Programming skills
        if (
          skillKey.startsWith("directional_") ||
          skillKey.startsWith("single_command_") ||
          skillKey.startsWith("sequential_") ||
          skillKey.startsWith("maze_") ||
          skillKey.startsWith("item_") ||
          skillKey.startsWith("path_") ||
          skillKey.startsWith("loops_") ||
          skillKey.startsWith("debugging_")
        ) {
          return "programming";
        }
        return null;
      };

      // Build discipline ID to slug mapping
      const disciplineIdToSlug: Record<string, DisciplineSlug> = {};
      for (const skill of transformedSkills) {
        const slug = getDisciplineFromSkillKey(skill.skillKey);
        if (slug && !disciplineIdToSlug[skill.disciplineId]) {
          disciplineIdToSlug[skill.disciplineId] = slug;
        }
      }

      // Create groups for each discipline
      const disciplineOrder: DisciplineSlug[] = ["math", "spelling", "patterns", "typing", "programming"];

      for (const slug of disciplineOrder) {
        // Find discipline ID for this slug
        const disciplineId = Object.entries(disciplineIdToSlug).find(
          ([, s]) => s === slug
        )?.[0];

        if (!disciplineId) {
          // Create empty group if no skills exist yet
          const info = DISCIPLINE_INFO[slug]!;
          disciplineGroups.push({
            disciplineId: slug, // Use slug as fallback
            disciplineSlug: slug,
            disciplineName: info.name,
            skills: [],
            totalChallenges: 0,
          });
          continue;
        }

        const disciplineSkills = transformedSkills
          .filter((s) => s.disciplineId === disciplineId)
          .sort((a, b) => a.difficultyOrder - b.difficultyOrder);

        const totalChallenges = disciplineSkills.reduce(
          (sum, skill) => sum + skill.challengeCount,
          0
        );

        const info = DISCIPLINE_INFO[slug]!;
        disciplineGroups.push({
          disciplineId,
          disciplineSlug: slug,
          disciplineName: info.name,
          skills: disciplineSkills,
          totalChallenges,
        });
      }

      setAllSkills(transformedSkills);
      setSkillGroups(disciplineGroups);
    } catch (err) {
      console.error("Failed to fetch skills:", err);
      setError(err instanceof Error ? err.message : "Failed to load skills");
      setAllSkills([]);
      setSkillGroups([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchSkillsData();
  }, [fetchSkillsData]);

  // Toggle discipline expansion
  const toggleDiscipline = useCallback((disciplineSlug: string) => {
    setExpandedDisciplines((prev) => {
      const next = new Set(prev);
      if (next.has(disciplineSlug)) {
        next.delete(disciplineSlug);
      } else {
        next.add(disciplineSlug);
      }
      return next;
    });
  }, []);

  // Expand/collapse all
  const expandAll = useCallback(() => {
    setExpandedDisciplines(
      new Set(["math", "spelling", "patterns", "typing", "programming"])
    );
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedDisciplines(new Set());
  }, []);

  // Get skills with coverage gaps (fewer than threshold challenges)
  const getSkillsWithGaps = useCallback(
    (threshold: number = 5) => {
      return allSkills.filter((skill) => skill.challengeCount < threshold);
    },
    [allSkills]
  );

  // Get total stats
  const getTotalStats = useCallback(() => {
    const totalSkills = allSkills.length;
    const totalChallenges = allSkills.reduce((sum, s) => sum + s.challengeCount, 0);
    const skillsWithContent = allSkills.filter((s) => s.challengeCount > 0).length;
    const coveragePercent =
      totalSkills > 0 ? Math.round((skillsWithContent / totalSkills) * 100) : 0;

    return {
      totalSkills,
      totalChallenges,
      skillsWithContent,
      coveragePercent,
    };
  }, [allSkills]);

  return {
    skillGroups,
    allSkills,
    isLoading,
    error,
    expandedDisciplines,
    toggleDiscipline,
    expandAll,
    collapseAll,
    getSkillsWithGaps,
    getTotalStats,
    refetch: fetchSkillsData,
  };
}
